import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { setToken } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuthCallback({ onLogin }) {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate('/login', { replace: true });
      return;
    }

    const sessionId = match[1];

    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/google/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
        });

        const data = await res.json().catch(() => ({}));

        if (res.ok && data.token) {
          // Set token + user state FIRST, then navigate.
          // navigate() is React Router-aware so it properly updates location state.
          setToken(data.token);
          onLogin(data.token, data.user);
          const dest = data.user.role === 'Client' ? '/client' : '/internal';
          navigate(dest, { replace: true });
        } else {
          // Surface the backend's actual error so admins know whether the issue is
          // an unapproved email, expired session, or a real backend failure.
          const msg = data.detail || `Login failed (HTTP ${res.status})`;
          navigate('/login', { replace: true, state: { error: msg } });
        }
      } catch (e) {
        // Network-level failure — fetch never reached the backend. Most likely
        // a misconfigured REACT_APP_BACKEND_URL or the API host is unreachable.
        // Surface enough detail for the user to share with us.
        const errMsg = (e && e.message) ? e.message : 'Network error';
        const detail = `Could not reach the authentication service.\n${errMsg}\nAPI: ${API || '(unset)'}`;
        // eslint-disable-next-line no-console
        console.error('[AuthCallback] fetch threw:', e, 'API URL:', API);
        navigate('/login', { replace: true, state: { error: detail } });
      }
    })();
  }, [navigate, onLogin]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-page">
      <div className="glass p-8 rounded-2xl text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
        <p className="text-secondary-fg">Signing you in...</p>
      </div>
    </div>
  );
}
